/**
 * Thin main-thread proxy for the WebLLM worker.
 * Does not import @mlc-ai/web-llm — keeps the main bundle small.
 */

import type { CustomRecordPayload, MainToWorker, WorkerToMain } from './engine-rpc-types.js';

export type ProgressCb = (report: { progress: number; text: string }) => void;

export interface WorkerLoadParams {
  modelId: string;
  cacheBackend: string;
  modelUrl?: string;
  customRecord?: CustomRecordPayload;
  onProgress?: ProgressCb;
  /** Keep previous engine serving until the new one is ready (in-worker swap). */
  keepPrevious?: boolean;
}

type EngineLike = {
  chat: {
    completions: {
      create: (req: any) => Promise<any>;
    };
  };
  unload: () => Promise<void> | void;
};

let nextId = 1;
let sharedProxy: WorkerEngineProxy | null = null;

export class WorkerEngineProxy implements EngineLike {
  private worker: Worker;
  private pending = new Map<
    number,
    {
      resolve: (v: any) => void;
      reject: (e: Error) => void;
      onProgress?: ProgressCb;
      onChunk?: (delta: string) => void;
    }
  >();

  constructor(worker: Worker) {
    this.worker = worker;
    this.worker.onmessage = (event: MessageEvent<WorkerToMain>) => {
      const msg = event.data;
      const entry = this.pending.get(msg.id);

      switch (msg.type) {
        case 'progress':
          entry?.onProgress?.({ progress: msg.progress, text: msg.text });
          break;
        case 'initOk':
          this.pending.delete(msg.id);
          entry?.resolve({ fromCache: msg.fromCache });
          break;
        case 'initErr':
        case 'chatErr':
        case 'cacheErr':
          this.pending.delete(msg.id);
          entry?.reject(new Error(msg.message));
          break;
        case 'chatChunk':
          entry?.onChunk?.(msg.delta);
          break;
        case 'chatChunks':
          for (const d of msg.deltas) {
            entry?.onChunk?.(d);
          }
          break;
        case 'chatOk':
          this.pending.delete(msg.id);
          entry?.resolve(msg.content);
          break;
        case 'unloadOk':
        case 'readyOk':
          this.pending.delete(msg.id);
          entry?.resolve(undefined);
          break;
        case 'cacheOk':
          this.pending.delete(msg.id);
          entry?.resolve(msg.cached);
          break;
        default:
          break;
      }
    };
  }

  private call<T>(
    msg: Record<string, unknown>,
    extras?: { onProgress?: ProgressCb; onChunk?: (delta: string) => void }
  ): Promise<T> {
    const id = nextId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, {
        resolve,
        reject,
        onProgress: extras?.onProgress,
        onChunk: extras?.onChunk,
      });
      this.worker.postMessage({ ...msg, id } as MainToWorker);
    });
  }

  /** Ensure the worker is spun up (parses bootstrap JS) without loading a model. */
  async ping(): Promise<void> {
    await this.call({ type: 'ping' });
  }

  async init(params: WorkerLoadParams): Promise<{ fromCache: boolean }> {
    return this.call(
      {
        type: 'init',
        modelId: params.modelId,
        cacheBackend: params.cacheBackend,
        modelUrl: params.modelUrl,
        customRecord: params.customRecord,
        keepPrevious: params.keepPrevious === true,
      },
      { onProgress: params.onProgress }
    );
  }

  async hasModelInCache(params: Omit<WorkerLoadParams, 'onProgress' | 'keepPrevious'>): Promise<boolean> {
    return this.call({
      type: 'hasModelInCache',
      modelId: params.modelId,
      cacheBackend: params.cacheBackend,
      modelUrl: params.modelUrl,
      customRecord: params.customRecord,
    });
  }

  chat = {
    completions: {
      create: async (req: any) => {
        const stream = !!req.stream;
        const { messages, stream: _s, ...extras } = req;

        if (stream) {
          const queue: string[] = [];
          let finished = false;
          let error: Error | null = null;
          let wake: (() => void) | null = null;
          const kick = () => {
            wake?.();
            wake = null;
          };

          const done = this.call<string>(
            { type: 'chat', messages, stream: true, extras },
            {
              onChunk: (delta) => {
                queue.push(delta);
                kick();
              },
            }
          )
            .then((content) => {
              finished = true;
              kick();
              return content;
            })
            .catch((e) => {
              error = e instanceof Error ? e : new Error(String(e));
              finished = true;
              kick();
              throw error;
            });

          async function* gen() {
            while (!finished || queue.length > 0) {
              while (queue.length > 0) {
                yield { choices: [{ delta: { content: queue.shift()! } }] };
              }
              if (!finished) {
                await new Promise<void>((r) => {
                  wake = r;
                });
              }
            }
            if (error) throw error;
            await done;
          }

          return gen();
        }

        const content = await this.call<string>({
          type: 'chat',
          messages,
          stream: false,
          extras,
        });
        return { choices: [{ message: { content } }] };
      },
    },
  };

  async unload(): Promise<void> {
    await this.call({ type: 'unload' });
  }

  terminate(): void {
    this.worker.terminate();
  }
}

export function createWorkerEngineProxy(): WorkerEngineProxy {
  const worker = new Worker(new URL('./engine.worker.ts', import.meta.url), {
    type: 'module',
  });
  return new WorkerEngineProxy(worker);
}

/**
 * Create (or reuse) a shared worker proxy and ping it so JS parse starts early.
 * Does not load a model.
 */
export async function preloadWorkerEngine(): Promise<WorkerEngineProxy> {
  if (!sharedProxy) {
    sharedProxy = createWorkerEngineProxy();
    try {
      await sharedProxy.ping();
    } catch {
      /* ping is best-effort */
    }
  }
  return sharedProxy;
}

/** Take ownership of the shared preloaded proxy (or create a new one). */
export function takeOrCreateWorkerProxy(): WorkerEngineProxy {
  if (sharedProxy) {
    const p = sharedProxy;
    sharedProxy = null;
    return p;
  }
  return createWorkerEngineProxy();
}

export function clearSharedWorkerProxy(): void {
  if (sharedProxy) {
    try {
      sharedProxy.terminate();
    } catch {
      /* ignore */
    }
    sharedProxy = null;
  }
}
